import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Trash2, ThumbsUp, ThumbsDown, Send, Clock, Copy, Loader2, PenLine, Save, Facebook, Instagram, Image as ImageIcon, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { SITE } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLATFORMS = ["Facebook / Instagram", "Google Ads", "Instagram Reel script", "LinkedIn"];
const STATUS_COLORS = { generating: "bg-blue-50 text-blue-600", draft: "bg-amber-50 text-clay", scheduled: "bg-violet-50 text-violet-600", published: "bg-green-50 text-green-700", failed: "bg-red-50 text-red-600" };

const copyNow = (text, msg = "Caption copied") => {
  Promise.resolve(navigator.clipboard?.writeText(text)).then(() => toast.success(msg)).catch(() => toast.error("Couldn't copy — long-press to copy manually."));
};

const stripTags = (s) => (s || "").replace(/<[^>]+>/g, "");
const decodeEntities = (s) => (s || "")
  .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&mdash;/g, "—").replace(/&ndash;/g, "–")
  .replace(/&rsquo;|&#8217;|&#39;/g, "’").replace(/&lsquo;|&#8216;/g, "‘")
  .replace(/&ldquo;|&#8220;/g, "“").replace(/&rdquo;|&#8221;/g, "”").replace(/&quot;/g, '"').replace(/&hellip;/g, "…");

// Instagram caption limit is 2,200 chars; we stay safely under.
const IG_LIMIT = 2100;

// Convert the blog HTML body into clean plain text for a social caption.
const bodyToPlainText = (d) => {
  const s = (d.body || "")
    .replace(/<h2[^>]*>(.*?)<\/h2>/gis, (_, t) => `\n\n${stripTags(t).trim()}\n`)
    .replace(/<h3[^>]*>(.*?)<\/h3>/gis, (_, t) => `\n\n${stripTags(t).trim()}\n`)
    .replace(/<li[^>]*>(.*?)<\/li>/gis, (_, t) => `\n• ${stripTags(t).trim()}`)
    .replace(/<\/(p|ul|ol|blockquote|div)>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n");
  return decodeEntities(stripTags(s)).replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
};

// Full-story caption: title + the whole article (trimmed to fit Instagram) +
// "Read the full blog" link + hashtags. Tapping "…more" expands the story.
const socialCaption = (d) => {
  const url = `${SITE.domain}/blog/${d.slug}`;
  const tags = (d.tags || []).slice(0, 4).map((t) => "#" + t.replace(/[^a-zA-Z0-9]/g, "")).join(" ");
  const hashtags = `#Waterbury #Therapy #MentalHealth #Connecticut ${tags}`.trim();
  const link = `📖 Read the full blog: ${url}`;
  const head = `${d.title}\n\n`;
  let body = bodyToPlainText(d);
  const reserve = head.length + link.length + hashtags.length + 8;
  const room = Math.max(300, IG_LIMIT - reserve);
  if (body.length > room) {
    let cut = body.slice(0, room);
    const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("\n"));
    if (stop > room * 0.5) cut = cut.slice(0, stop + 1);
    body = `${cut.trim()} …`;
  }
  return `${head}${body}\n\n${link}\n\n${hashtags}`.trim();
};

// Wrap a string into lines that fit maxWidth on the given canvas context.
const thumb = (url) => url.replace("w=1200", "w=300");

// Tap-to-choose grid of cover pictures shown under the post preview.
const CoverGrid = ({ options, current, disabled, onPick }) => (
  <div className="mt-3" data-testid="cover-grid">
    <p className="font-sans text-xs text-ink-soft">Or tap a picture you like:</p>
    {options === null ? (
      <div className="mt-2 flex items-center gap-2 font-sans text-xs text-ink-soft" data-testid="cover-grid-loading"><Loader2 size={13} className="animate-spin" /> Loading pictures…</div>
    ) : (
      <div className="mt-2 grid grid-cols-4 gap-2">
        {options.map((o, i) => (
          <button key={o.url} type="button" disabled={disabled} onClick={() => onPick(o.url)} data-testid={`cover-option-${i}`}
            className={`relative aspect-square overflow-hidden rounded transition-transform duration-150 hover:scale-[1.04] disabled:opacity-50 ${o.url === current ? "ring-2 ring-[#2C2A26] ring-offset-2" : ""}`}>
            <img src={thumb(o.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
            {o.usedElsewhere && <span className="absolute bottom-0 left-0 right-0 bg-black/60 py-0.5 text-center font-sans text-[9px] text-white">used</span>}
            {o.url === current && <span className="absolute left-1 top-1 rounded-full bg-white/90 px-1.5 font-sans text-[9px] text-[#2C2A26]">current</span>}
          </button>
        ))}
      </div>
    )}
  </div>
);

const wrapText = (ctx, text, maxWidth) => {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
};

// Build a branded 1080x1350 (4:5, Instagram's feed size, so nothing gets cropped) graphic:
// the blog cover photo with the article title + "READ THE BLOG" baked on.
// story=true builds a 1080x1920 (9:16) Story version with empty space under the text for the link sticker.
const composeSocialImage = async (d, story = false) => {
  const W = 1080, H = story ? 1920 : 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#2f3b33"; ctx.fillRect(0, 0, W, H);
  if (d.coverImage) {
    try {
      const res = await fetch(d.coverImage);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const img = await new Promise((resolve, reject) => { const im = new Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = objUrl; });
      const scale = Math.max(W / img.width, H / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      URL.revokeObjectURL(objUrl);
    } catch { /* keep solid background */ }
  }
  const g = ctx.createLinearGradient(0, H * (story ? 0.3 : 0.28), 0, H);
  g.addColorStop(0, "rgba(24,28,22,0)");
  g.addColorStop(0.5, "rgba(24,28,22,0.45)");
  g.addColorStop(1, "rgba(24,28,22,0.93)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  try { await document.fonts.ready; } catch { /* system fallback */ }
  const pad = 110;
  const readBaseline = story ? H - 600 : H - 120;
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#E7C9A3";
  ctx.font = "600 34px Figtree, Arial, sans-serif";
  ctx.fillText("READ THE BLOG  →", pad, readBaseline);
  ctx.fillStyle = "rgba(247,243,236,0.78)";
  ctx.font = "500 27px Figtree, Arial, sans-serif";
  ctx.fillText("letgowellnessseo.com", pad, readBaseline + 40);
  if (story) {
    ctx.fillStyle = "#E7C9A3";
    ctx.font = "600 30px Figtree, Arial, sans-serif";
    ctx.fillText("TAP THE LINK BELOW  ↓", pad, readBaseline + 150);
  }
  ctx.fillStyle = "#F7F3EC";
  let size = 68;
  let lines;
  do {
    ctx.font = `600 ${size}px 'Cormorant Garamond', Georgia, serif`;
    lines = wrapText(ctx, d.title, W - pad * 2);
    size -= 4;
  } while ((lines.length > 4 || lines.some((l) => ctx.measureText(l).width > W - pad * 2)) && size >= 40);
  const lineH = Math.round((size + 4) * 1.12);
  let ty = readBaseline - 78 - (lines.length - 1) * lineH;
  for (const ln of lines) { ctx.fillText(ln, pad, ty); ty += lineH; }
  ctx.fillStyle = "rgba(247,243,236,0.82)";
  ctx.font = "600 24px Figtree, Arial, sans-serif";
  ctx.fillText("LET GO WELLNESS · WATERBURY, CT", pad, readBaseline - 78 - (lines.length - 1) * lineH - 74);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
  const file = new File([blob], `${d.slug || "post"}${story ? "-story" : ""}.jpg`, { type: "image/jpeg" });
  return { file, dataUrl };
};

// Feed post (4:5) + Story (9:16) pictures, built together so sharing stays one tap on iPad.
const buildSocialImages = async (d) => {
  const [post, story] = await Promise.all([composeSocialImage(d), composeSocialImage(d, true)]);
  return { imageFile: post.file, previewUrl: post.dataUrl, storyFile: story.file, storyUrl: story.dataUrl };
};

const blogFb = (d) => `${d.title}\n\n${d.excerpt || ""}\n\nRead more: ${SITE.domain}/blog/${d.slug}\n\nLet Go Wellness · Therapy in Waterbury, CT · ${SITE.phoneOffice}`;
const blogIg = (d) => `${d.title}\n\n${d.excerpt || ""}\n\nRead the full post on our website (link in bio).\n\n#Waterbury #Therapy #MentalHealth #Connecticut ${(d.tags || []).slice(0, 4).map((t) => "#" + t.replace(/[^a-zA-Z0-9]/g, "")).join(" ")}`;

// Manual share bar — post to FB/IG in seconds, no API/token needed.
const ShareBar = ({ fbCaption, igCaption, imageUrl, shareUrl }) => (
  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3" data-testid="share-bar">
    <span className="overline mr-1">Share manually</span>
    <button onClick={() => copyNow(fbCaption, "Facebook caption copied")} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-sans text-xs text-ink-soft hover:bg-linen"><Copy size={12} /> FB caption</button>
    <button onClick={() => copyNow(igCaption, "Instagram caption copied")} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-sans text-xs text-ink-soft hover:bg-linen"><Copy size={12} /> IG caption</button>
    {imageUrl && <a href={imageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-sans text-xs text-ink-soft hover:bg-linen"><ImageIcon size={12} /> Open image</a>}
    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl || SITE.domain)}`} target="_blank" rel="noreferrer" onClick={() => copyNow(fbCaption, "Caption copied — paste it in the Facebook box")} className="inline-flex items-center gap-1.5 rounded-full bg-[#1877F2] px-3 py-1.5 font-sans text-xs text-white hover:opacity-90"><Facebook size={12} /> Share to Facebook</a>
    <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" onClick={() => copyNow(igCaption, "Caption copied — paste it in Instagram")} className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] px-3 py-1.5 font-sans text-xs text-white hover:opacity-90"><Instagram size={12} /> Open Instagram</a>
  </div>
);

const AdminNav = ({ email, onLogout }) => (
  <header className="sticky top-0 z-20 border-b border-border bg-linen/85 backdrop-blur-lg">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
      <div className="flex items-center gap-6">
        <div className="font-serif text-xl text-ink">Let Go <span className="italic text-clay">Wellness</span> · Admin</div>
        <nav className="flex gap-1">
          <Link to="/admin" data-testid="nav-submissions" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">Submissions</Link>
          <Link to="/admin/ai" data-testid="nav-ai-studio" className="rounded-full bg-ink px-4 py-1.5 font-sans text-xs text-linen">AI Studio</Link>
          <Link to="/admin/subscribers" data-testid="nav-subscribers" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">Subscribers</Link>
        </nav>
      </div>
      <div className="flex items-center gap-4">
        <span className="hidden font-sans text-xs text-muted-foreground sm:inline">{email}</span>
        <button data-testid="admin-logout" onClick={onLogout} className="rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft transition-colors hover:bg-white">Sign out</button>
      </div>
    </div>
  </header>
);

export default function AiStudio() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("blog");
  const [drafts, setDrafts] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [blogForm, setBlogForm] = useState({ topic: "", keyword: "" });
  const [adForm, setAdForm] = useState({ topic: "", platform: PLATFORMS[0] });
  const [busy, setBusy] = useState(false);
  const [social, setSocial] = useState(null);
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [calSel, setCalSel] = useState(null);
  const [editing, setEditing] = useState(null); // {id, title, excerpt, tags, body}
  const [shareHelper, setShareHelper] = useState(null); // {caption, url, title, slug}
  const [coverOptions, setCoverOptions] = useState(null);
  const [savedTodayNotes, setSavedTodayNotes] = useState(() => {
    try { const n = new Date(); const tk = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; return localStorage.getItem(`lgw_notes_saved_${tk}`) === "1"; } catch { return false; }
  });
  const pollRef = useRef(null);

  const loadDrafts = useCallback(async () => {
    const { data } = await api.get("/admin/ai/drafts");
    setDrafts(data);
    return data;
  }, []);
  const loadPrefs = useCallback(async () => {
    const { data } = await api.get("/admin/ai/preferences");
    setPrefs(data);
  }, []);

  useEffect(() => { loadDrafts(); loadPrefs(); }, [loadDrafts, loadPrefs]);

  useEffect(() => { api.get("/admin/social/status").then((r) => setSocial(r.data)).catch(() => {}); }, []);

  const postToSocial = async (id) => {
    try {
      await api.post(`/admin/ai/drafts/${id}/social`, { variantIndex: 0 });
      toast.success("Posted to Facebook & Instagram.");
      loadDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post to social.");
    }
  };

  const genToday = async () => {
    const q = (prefs?.topicQueue && prefs.topicQueue.length) ? prefs.topicQueue : ["Anxiety therapy in Waterbury, CT: what to expect"];
    const topic = q[new Date().getDate() % q.length];
    setBusy(true);
    try {
      await api.post("/admin/ai/blog", { topic });
      toast.success("Drafting today's article… ready in ~30 seconds.");
      await loadDrafts();
    } catch { toast.error("Could not generate. Try again."); } finally { setBusy(false); }
  };

  // ONE-TAP: copy caption to clipboard, then open a helper dialog that walks you
  // through attaching the SAME blog photo and pasting the caption in Instagram/Facebook.
  const shareToSocial = async (d) => {
    const caption = socialCaption(d);
    try { await navigator.clipboard.writeText(caption); } catch { /* non-fatal */ }
    // Open the dialog immediately with the plain cover as a placeholder…
    setShareHelper({ caption, previewUrl: d?.coverImage, imageFile: null, title: d.title, slug: d.slug, id: d.id, tags: d.tags, coverImage: d.coverImage, building: true });
    loadCoverOptions(d.id);
    // …then compose the branded "Read the blog" graphic and swap it in.
    try {
      const imgs = await buildSocialImages(d);
      setShareHelper((h) => (h && h.slug === d.slug ? { ...h, ...imgs, building: false } : h));
    } catch {
      setShareHelper((h) => (h && h.slug === d.slug ? { ...h, building: false } : h));
    }
  };

  const loadCoverOptions = async (id) => {
    setCoverOptions(null);
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/ai/drafts/${id}/cover-options`);
      setCoverOptions(data.options);
    } catch { setCoverOptions([]); }
  };

  // Rebuild the branded graphic around a new cover (from shuffle or the picture grid).
  const changeCover = async (request) => {
    if (!shareHelper?.id) return;
    const id = shareHelper.id;
    setShareHelper((h) => (h ? { ...h, building: true } : h));
    try {
      const { data } = await request(id);
      const cover = data.coverImage;
      const imgs = await buildSocialImages({ title: shareHelper.title, slug: shareHelper.slug, coverImage: cover, tags: shareHelper.tags });
      setShareHelper((h) => (h && h.id === id ? { ...h, coverImage: cover, ...imgs, building: false } : h));
      setCoverOptions((opts) => (opts && !opts.some((o) => o.url === cover) ? [{ url: cover, usedElsewhere: false }, ...opts.slice(0, -1)] : opts));
      loadDrafts();
      toast.success("New picture ready!");
    } catch {
      setShareHelper((h) => (h ? { ...h, building: false } : h));
      toast.error("Couldn't change the picture — try again.");
    }
  };

  // "Try another picture" — get a new topic-matched cover and rebuild the graphic.
  const shuffleCover = () => changeCover((id) => api.post(`/admin/ai/drafts/${id}/shuffle-cover`));
  const chooseCover = (url) => changeCover((id) => api.put(`/admin/ai/drafts/${id}/cover`, { coverImage: url }));

  const copyBlogLink = async () => {
    const link = `${SITE.domain}/blog/${shareHelper.slug}`;
    const el = document.querySelector("[data-testid='share-blog-link']");
    if (el) { el.focus(); el.select(); }
    try { await navigator.clipboard.writeText(link); toast.success("Blog link copied! Paste it into the Story link sticker."); return; }
    catch { /* fall through */ }
    try { if (el && document.execCommand("copy")) { toast.success("Blog link copied! Paste it into the Story link sticker."); return; } } catch { /* noop */ }
    toast("Link is selected above — press and hold it, then tap Copy.");
  };

  const copyCaption = async () => {
    const ta = document.querySelector("[data-testid='share-caption']");
    if (ta) { ta.focus(); ta.select(); }
    try {
      await navigator.clipboard.writeText(shareHelper.caption);
      toast.success("Caption copied! Paste it in the Instagram/Facebook caption box.");
      return;
    } catch { /* try legacy path */ }
    try {
      if (ta && document.execCommand("copy")) {
        toast.success("Caption copied! Paste it in the Instagram/Facebook caption box.");
        return;
      }
    } catch { /* fall through */ }
    toast("Text is selected above — press and hold it, then tap Copy.");
  };

  const sharePhoto = async (story = false) => {
    const h = shareHelper;
    if (!h) return;
    const url = story ? h.storyUrl : h.previewUrl;
    try {
      let file = story ? h.storyFile : h.imageFile;
      if (!file && url) {
        const res = await fetch(url);
        const blob = await res.blob();
        file = new File([blob], `${h.slug || "post"}.jpg`, { type: blob.type || "image/jpeg" });
      }
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: h.title });
        return;
      }
      const a = document.createElement("a");
      a.href = file ? URL.createObjectURL(file) : url;
      a.download = file ? file.name : `${h.slug || "post"}.jpg`;
      a.click();
      toast.success("Image saved to your device — attach it in Instagram/Facebook.");
    } catch (e) {
      if (e && e.name === "AbortError") return;
      if (url) window.open(url, "_blank");
      toast("Press and hold the image above, then tap 'Save to Photos'.");
    }
  };

  // Poll while anything is generating
  useEffect(() => {
    const generating = drafts.some((d) => d.status === "generating");
    if (generating && !pollRef.current) {
      pollRef.current = setInterval(loadDrafts, 3000);
    } else if (!generating && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !drafts.some((d) => d.status === "generating")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [drafts, loadDrafts]);

  const genBlog = async () => {
    if (!blogForm.topic.trim()) return toast.error("Add a topic first.");
    setBusy(true);
    try {
      await api.post("/admin/ai/blog", blogForm);
      toast.success("Drafting… Waleska's AI is writing. This takes ~30 seconds.");
      setBlogForm({ topic: "", keyword: "" });
      await loadDrafts();
    } catch { toast.error("Could not start generation."); } finally { setBusy(false); }
  };

  const genAds = async () => {
    if (!adForm.topic.trim()) return toast.error("Add an angle first.");
    setBusy(true);
    try {
      await api.post("/admin/ai/ads", adForm);
      toast.success("Ad copy ready.");
      setAdForm({ ...adForm, topic: "" });
      await loadDrafts();
    } catch { toast.error("Generation failed. Try again."); } finally { setBusy(false); }
  };

  const publishNow = async (id) => { await api.post(`/admin/ai/drafts/${id}/publish`, {}); toast.success("Published to your live blog."); loadDrafts(); };
  const schedule = async (id, date) => {
    if (!date) return;
    await api.post(`/admin/ai/drafts/${id}/publish`, { publishAt: new Date(date).toISOString() });
    toast.success("Scheduled."); loadDrafts();
  };
  const feedback = async (id, rating) => {
    const notes = rating === "down" ? window.prompt("What should the AI do differently next time?") : window.prompt("What did you love? (optional — helps it learn)");
    await api.post(`/admin/ai/drafts/${id}/feedback`, { rating, notes: notes || "" });
    toast.success("Thanks — noted for next time."); loadPrefs(); loadDrafts();
  };
  const del = async (id) => { if (!window.confirm("Delete this draft?")) return; await api.delete(`/admin/ai/drafts/${id}`); loadDrafts(); };
  const saveEdit = async () => {
    const { id, ...body } = editing;
    body.tags = body.tags.split(",").map((t) => t.trim()).filter(Boolean);
    await api.patch(`/admin/ai/drafts/${id}`, body);
    toast.success("Saved."); setEditing(null); loadDrafts();
  };
  const savePrefs = async () => {
    await api.put("/admin/ai/preferences", {
      tone: prefs.tone || "",
      dos: (prefs.dosText ?? (prefs.dos || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
      donts: (prefs.dontsText ?? (prefs.donts || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
      topicQueue: (prefs.queueText ?? (prefs.topicQueue || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
      autopilot: !!prefs.autopilot,
      autoPublish: !!prefs.autoPublish,
    });
    toast.success("Preferences saved."); loadPrefs();
  };

  const blogDrafts = drafts.filter((d) => d.type === "blog");
  const adDrafts = drafts.filter((d) => d.type === "ad");
  const dateKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const draftDate = (d) => (d.publishAt || d.createdAt || "").slice(0, 10);
  const byDay = {};
  blogDrafts.forEach((d) => { const k = draftDate(d); if (k) (byDay[k] = byDay[k] || []).push(d); });
  const firstDow = new Date(calMonth.y, calMonth.m, 1).getDay();
  const numDays = new Date(calMonth.y, calMonth.m + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: numDays }, (_, i) => i + 1)];
  const todayKey = dateKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const todaysDraft = blogDrafts.find((d) => draftDate(d) === todayKey && d.body && d.status !== "generating" && d.status !== "failed");
  const postTodayToSocial = async () => {
    const todays = blogDrafts.filter((d) => draftDate(d) === todayKey && d.status !== "generating" && d.status !== "failed" && d.body);
    if (todays.length === 0) return toast.error("No article ready for today yet — tap “Generate today’s article” first.");
    await shareToSocial(todays[todays.length - 1]);
    try { localStorage.setItem(`lgw_notes_saved_${todayKey}`, "1"); } catch {}
    setSavedTodayNotes(true);
  };
  const dismissReminder = () => { try { localStorage.setItem(`lgw_notes_saved_${todayKey}`, "1"); } catch {} setSavedTodayNotes(true); };

  return (
    <div className="min-h-screen bg-linen">
      <Seo title="AI Studio — Let Go Wellness Admin" description="AI content engine." path="/admin/ai" />
      <AdminNav email={user?.email} onLogout={logout} />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <Sparkles className="text-clay" size={26} />
          <div>
            <h1 className="font-serif text-4xl text-ink">AI Studio</h1>
            <p className="mt-1 font-sans text-sm text-muted-foreground">On-brand SEO posts & ad copy, written by Claude — trained on your clinic, drafted for your approval.</p>
          </div>
        </div>

        {todaysDraft && !savedTodayNotes && (
          <div data-testid="notes-reminder" className="mb-6 flex flex-wrap items-center gap-3 border border-clay/40 bg-amber-50/70 p-4">
            <Instagram size={18} className="shrink-0 text-clay" />
            <p className="flex-1 font-sans text-sm text-ink-soft">Today's article is ready — <strong className="text-ink">post it to Instagram & Facebook</strong>, then share it to your <strong className="text-ink">Story with a link sticker 👉 tappable link to the blog</strong>.</p>
            <button data-testid="notes-reminder-save" onClick={postTodayToSocial} className="inline-flex items-center gap-1.5 rounded-full bg-[#E1306C] px-4 py-2 font-sans text-xs text-white hover:opacity-90"><Instagram size={13} /> Post it now</button>
            <button data-testid="notes-reminder-dismiss" onClick={dismissReminder} className="rounded-full border border-border px-3 py-2 font-sans text-xs text-muted-foreground transition-colors hover:text-ink">Dismiss</button>
          </div>
        )}

        <div className="mb-8 flex gap-2">
          {[["blog", "SEO Blog Posts"], ["cal", "Content Calendar"], ["ads", "Ad & Social Copy"], ["prefs", "Brand & Autopilot"]].map(([k, l]) => (
            <button key={k} data-testid={`aistudio-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-4 py-2 font-sans text-xs transition-colors ${tab === k ? "bg-ink text-linen" : "border border-border bg-white text-ink-soft hover:bg-white"}`}>{l}</button>
          ))}
        </div>

        {social && (
          <div data-testid="social-status" className={`mb-6 border p-4 font-sans text-sm ${social.configured ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            {social.configured
              ? <><strong>Facebook &amp; Instagram connected.</strong> Publishing a post here also shares it to your Page{social.instagram ? " and Instagram" : ""} automatically.</>
              : <><strong>Facebook &amp; Instagram not connected yet.</strong> Send your Meta credentials and auto-posting turns on — publishing here will then share to your Page &amp; Instagram.</>}
          </div>
        )}

        {tab === "blog" && (
          <div className="space-y-8">
            <div className="border border-border bg-white p-6">
              <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
                <div><Label className="font-sans text-xs text-ink-soft">Blog topic</Label>
                  <Input data-testid="blog-topic" value={blogForm.topic} onChange={(e) => setBlogForm({ ...blogForm, topic: e.target.value })} placeholder="e.g. Signs your teen may need therapy" className="mt-1.5 rounded-none border-border" /></div>
                <div><Label className="font-sans text-xs text-ink-soft">Target keyword (optional)</Label>
                  <Input data-testid="blog-keyword" value={blogForm.keyword} onChange={(e) => setBlogForm({ ...blogForm, keyword: e.target.value })} placeholder="teen therapy Waterbury CT" className="mt-1.5 rounded-none border-border" /></div>
                <button data-testid="blog-generate" onClick={genBlog} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-2.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85 disabled:opacity-60">
                  <Sparkles size={15} /> Generate</button>
              </div>
            </div>

            <div className="space-y-4" data-testid="blog-drafts">
              {blogDrafts.length === 0 && <p className="font-sans text-sm text-muted-foreground">No posts yet. Generate your first one above.</p>}
              {blogDrafts.map((d) => (
                <div key={d.id} data-testid={`draft-${d.id}`} className="border border-border bg-white p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    {d.coverImage && <img src={d.coverImage} alt="" data-testid={`draft-cover-${d.id}`} className="h-20 w-28 shrink-0 object-cover" loading="lazy" />}
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <span className={`rounded-full px-2.5 py-0.5 font-sans text-[10px] uppercase tracking-wide ${STATUS_COLORS[d.status]}`}>{d.status}{d.createdBy === "autopilot" ? " · auto" : ""}</span>
                        {d.status === "generating" && <Loader2 size={14} className="animate-spin text-blue-500" />}
                      </div>
                      {d.status === "generating" ? (
                        <p className="font-serif text-xl text-ink">Writing: “{d.topic}”…</p>
                      ) : d.status === "failed" ? (
                        <p className="font-sans text-sm text-red-600">Generation failed for “{d.topic}”. {d.error}</p>
                      ) : (
                        <>
                          <h2 className="font-serif text-2xl text-ink">{d.title}</h2>
                          <p className="mt-1 font-sans text-xs text-muted-foreground">/{d.slug} · {d.readMinutes} min · {(d.tags || []).join(", ")}</p>
                          <p className="mt-2 font-sans text-sm text-ink-soft">{d.excerpt}</p>
                        </>
                      )}
                    </div>
                  </div>

                  {d.status !== "generating" && d.status !== "failed" && (
                    <>
                      {editing?.id === d.id ? (
                        <div className="mt-4 space-y-3 border-t border-border pt-4">
                          <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-none border-border font-serif" />
                          <Input value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="rounded-none border-border" placeholder="Excerpt" />
                          <Input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className="rounded-none border-border" placeholder="tags, comma separated" />
                          <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={12} className="rounded-none border-border font-mono text-xs" />
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-sans text-xs text-linen"><Save size={13} /> Save</button>
                            <button onClick={() => setEditing(null)} className="rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <details className="mt-3 border-t border-border pt-3">
                          <summary className="cursor-pointer font-sans text-xs text-clay">Preview full post</summary>
                          <div className="prose-editorial mt-4 max-w-none" dangerouslySetInnerHTML={{ __html: d.body }} />
                        </details>
                      )}

                      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
                        {d.status === "published" ? (
                          <a href={`/blog/${d.slug}`} target="_blank" rel="noreferrer" className="rounded-full bg-green-600 px-4 py-2 font-sans text-xs text-white">View live post ↗</a>
                        ) : (
                          <>
                            <button data-testid={`publish-${d.id}`} onClick={() => publishNow(d.id)} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-sans text-xs text-linen hover:bg-ink/85"><Send size={13} /> Publish now</button>
                            <label className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 font-sans text-xs text-ink-soft"><Clock size={13} /><input type="date" onChange={(e) => schedule(d.id, e.target.value)} className="bg-transparent text-xs outline-none" /></label>
                          </>
                        )}
                        <button onClick={() => setEditing({ id: d.id, title: d.title, excerpt: d.excerpt, tags: (d.tags || []).join(", "), body: d.body })} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 font-sans text-xs text-ink-soft hover:bg-linen"><PenLine size={13} /> Edit</button>
                        <button onClick={() => feedback(d.id, "up")} className={`rounded-full border border-border p-2 ${d.rating === "up" ? "text-green-600" : "text-muted-foreground"} hover:bg-linen`}><ThumbsUp size={14} /></button>
                        <button onClick={() => feedback(d.id, "down")} className={`rounded-full border border-border p-2 ${d.rating === "down" ? "text-red-600" : "text-muted-foreground"} hover:bg-linen`}><ThumbsDown size={14} /></button>
                        <button onClick={() => del(d.id)} className="rounded-full border border-border p-2 text-muted-foreground hover:border-destructive hover:text-destructive"><Trash2 size={14} /></button>
                        <button data-testid={`post-social-${d.id}`} onClick={() => shareToSocial(d)} className="inline-flex items-center gap-1.5 rounded-full bg-[#E1306C] px-4 py-2 font-sans text-xs text-white hover:opacity-90"><Instagram size={13} /> Post to Instagram / Facebook</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "cal" && (
          <div data-testid="calendar-tab">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-3xl text-ink">{new Date(calMonth.y, calMonth.m).toLocaleString("en-US", { month: "long", year: "numeric" })}</h2>
              <div className="flex gap-2 font-sans">
                <button data-testid="cal-prev" onClick={() => setCalMonth((p) => { const m = p.m - 1; return m < 0 ? { y: p.y - 1, m: 11 } : { y: p.y, m }; })} className="rounded-full border border-border px-3 py-1.5 text-xs text-ink-soft hover:bg-white">‹ Prev</button>
                <button onClick={() => setCalMonth(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; })} className="rounded-full border border-border px-3 py-1.5 text-xs text-ink-soft hover:bg-white">Today</button>
                <button data-testid="cal-next" onClick={() => setCalMonth((p) => { const m = p.m + 1; return m > 11 ? { y: p.y + 1, m: 0 } : { y: p.y, m }; })} className="rounded-full border border-border px-3 py-1.5 text-xs text-ink-soft hover:bg-white">Next ›</button>
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-sans text-xs text-muted-foreground">Autopilot drafts one article every day. Tap any article to review it and post it to Instagram & Facebook.</p>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="cal-save-today-notes" onClick={postTodayToSocial} className="inline-flex items-center gap-2 rounded-full bg-[#E1306C] px-5 py-2.5 font-sans text-sm text-white transition-colors hover:opacity-90">
                  <Instagram size={15} /> Post today’s article
                </button>
                <button data-testid="cal-generate-today" onClick={genToday} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85 disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate today's article
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-px overflow-hidden border border-border bg-border">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => <div key={w} className="overline bg-white p-2 text-center">{w}</div>)}
              {cells.map((d, i) => {
                const k = d ? dateKey(calMonth.y, calMonth.m, d) : null;
                const items = k ? (byDay[k] || []) : [];
                return (
                  <div key={i} className={`min-h-[96px] p-1.5 ${d ? "bg-white" : "bg-linen/40"} ${k === todayKey ? "ring-1 ring-inset ring-clay" : ""}`}>
                    {d && <div className="mb-1 font-sans text-[11px] text-muted-foreground">{d}</div>}
                    {items.map((it) => (
                      <button key={it.id} data-testid={`cal-item-${it.id}`} onClick={() => setCalSel(it)} title={it.title || it.topic}
                        className={`mb-1 block w-full truncate rounded px-1.5 py-1 text-left font-sans text-[10px] ${STATUS_COLORS[it.status] || "bg-amber-50 text-clay"}`}>
                        {it.title || it.topic}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
            {calSel && (
              <div className="mt-6 border border-border bg-white p-6" data-testid="cal-detail">
                <div className="mb-2 flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 font-sans text-[10px] uppercase tracking-wide ${STATUS_COLORS[calSel.status]}`}>{calSel.status}</span>
                  <button onClick={() => setCalSel(null)} className="ml-auto font-sans text-xs text-muted-foreground hover:text-ink">Close ✕</button>
                </div>
                <h3 className="font-serif text-2xl text-ink">{calSel.title || calSel.topic}</h3>
                {calSel.excerpt && <p className="mt-2 font-sans text-sm text-ink-soft">{calSel.excerpt}</p>}
                {calSel.status !== "generating" && calSel.status !== "failed" && (
                  <>
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                      {calSel.status !== "published" && <button onClick={() => { publishNow(calSel.id); setCalSel(null); }} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-sans text-xs text-linen hover:bg-ink/85"><Send size={13} /> Publish now</button>}
                      <button data-testid="cal-post-social" onClick={() => shareToSocial(calSel)} className="inline-flex items-center gap-1.5 rounded-full bg-[#E1306C] px-4 py-2 font-sans text-xs text-white hover:opacity-90"><Instagram size={13} /> Post to Instagram / Facebook</button>
                      <label className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 font-sans text-xs text-ink-soft"><Clock size={13} /><input type="date" onChange={(e) => { schedule(calSel.id, e.target.value); }} className="bg-transparent text-xs outline-none" /></label>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "ads" && (
          <div className="space-y-8">
            <div className="border border-border bg-white p-6">
              <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
                <div><Label className="font-sans text-xs text-ink-soft">Ad angle / topic</Label>
                  <Input data-testid="ad-topic" value={adForm.topic} onChange={(e) => setAdForm({ ...adForm, topic: e.target.value })} placeholder="e.g. evening & telehealth openings for anxiety" className="mt-1.5 rounded-none border-border" /></div>
                <div><Label className="font-sans text-xs text-ink-soft">Platform</Label>
                  <Select value={adForm.platform} onValueChange={(v) => setAdForm({ ...adForm, platform: v })}>
                    <SelectTrigger data-testid="ad-platform" className="mt-1.5 rounded-none border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
                <button data-testid="ad-generate" onClick={genAds} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-2.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85 disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate</button>
              </div>
            </div>
            <div className="space-y-4" data-testid="ad-drafts">
              {adDrafts.length === 0 && <p className="font-sans text-sm text-muted-foreground">No ad copy yet.</p>}
              {adDrafts.map((d) => (
                <div key={d.id} className="border border-border bg-white p-6">
                  <div className="mb-3 flex items-center justify-between">
                    <div><span className="overline">{d.platform}</span><p className="font-serif text-lg text-ink">“{d.topic}”</p></div>
                    <div className="flex gap-2">
                      <button data-testid={`ad-post-social-${d.id}`} onClick={() => postToSocial(d.id)} disabled={!social?.configured}
                        title={social?.configured ? "Post to Facebook & Instagram" : "Connect Meta first"}
                        className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-2 font-sans text-xs text-linen transition-colors hover:bg-ink/85 disabled:opacity-40">
                        <Send size={13} /> Post to FB &amp; IG
                      </button>
                      <button onClick={() => feedback(d.id, "up")} className={`rounded-full border border-border p-2 ${d.rating === "up" ? "text-green-600" : "text-muted-foreground"}`}><ThumbsUp size={14} /></button>
                      <button onClick={() => feedback(d.id, "down")} className={`rounded-full border border-border p-2 ${d.rating === "down" ? "text-red-600" : "text-muted-foreground"}`}><ThumbsDown size={14} /></button>
                      <button onClick={() => del(d.id)} className="rounded-full border border-border p-2 text-muted-foreground hover:border-destructive hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(d.variants || []).map((v, i) => (
                      <div key={i} className="group relative border border-border bg-linen p-4">
                        <p className="font-serif text-base text-ink">{v.headline}</p>
                        <p className="mt-1.5 font-sans text-sm text-ink-soft">{v.body}</p>
                        <p className="mt-2 font-sans text-xs font-medium text-clay">→ {v.cta}</p>
                        <button onClick={() => { navigator.clipboard.writeText(`${v.headline}\n\n${v.body}\n\n${v.cta}`); toast.success("Copied"); }} className="absolute right-2 top-2 rounded-full border border-border bg-white p-1.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"><Copy size={12} /></button>
                      </div>
                    ))}
                  </div>
                  {(d.variants || []).length > 0 && (
                    <ShareBar
                      fbCaption={`${d.variants[0].headline}\n\n${d.variants[0].body}\n\n${d.variants[0].cta}\n\nLet Go Wellness · Therapy in Waterbury, CT · ${SITE.phoneOffice}`}
                      igCaption={`${d.variants[0].headline}\n\n${d.variants[0].body}\n\n${d.variants[0].cta}\n\n#Waterbury #Therapy #MentalHealth #Connecticut`}
                      shareUrl={SITE.domain}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "prefs" && prefs && (
          <div className="max-w-2xl space-y-6 border border-border bg-white p-8" data-testid="prefs-panel">
            <div><Label className="font-sans text-xs text-ink-soft">Tone / voice notes</Label>
              <Textarea value={prefs.tone || ""} onChange={(e) => setPrefs({ ...prefs, tone: e.target.value })} rows={2} placeholder="e.g. warm, plain-spoken, never clinical" className="mt-1.5 rounded-none border-border" /></div>
            <div><Label className="font-sans text-xs text-ink-soft">Always do (one per line)</Label>
              <Textarea value={prefs.dosText ?? (prefs.dos || []).join("\n")} onChange={(e) => setPrefs({ ...prefs, dosText: e.target.value })} rows={3} className="mt-1.5 rounded-none border-border" /></div>
            <div><Label className="font-sans text-xs text-ink-soft">Never do (one per line)</Label>
              <Textarea value={prefs.dontsText ?? (prefs.donts || []).join("\n")} onChange={(e) => setPrefs({ ...prefs, dontsText: e.target.value })} rows={3} className="mt-1.5 rounded-none border-border" /></div>
            <div><Label className="font-sans text-xs text-ink-soft">Autopilot topic queue (one per line)</Label>
              <Textarea value={prefs.queueText ?? (prefs.topicQueue || []).join("\n")} onChange={(e) => setPrefs({ ...prefs, queueText: e.target.value })} rows={5} className="mt-1.5 rounded-none border-border" /></div>
            <div className="flex items-center justify-between border-t border-border pt-5">
              <div><p className="font-sans text-sm text-ink">Daily autopilot</p><p className="font-sans text-xs text-muted-foreground">Every day, AI writes the next queued topic.</p></div>
              <Switch data-testid="autopilot-switch" checked={!!prefs.autopilot} onCheckedChange={(v) => setPrefs({ ...prefs, autopilot: v })} />
            </div>
            <div className="flex items-center justify-between border-t border-border pt-5">
              <div><p className="font-sans text-sm text-ink">Full auto-publish</p><p className="font-sans text-xs text-muted-foreground">When on, each daily article goes live automatically — no approval needed. Turn off to review drafts first.</p></div>
              <Switch data-testid="autopublish-switch" checked={!!prefs.autoPublish} onCheckedChange={(v) => setPrefs({ ...prefs, autoPublish: v })} />
            </div>
            {(prefs.learnedNotes || []).length > 0 && (
              <div className="border-t border-border pt-4"><Label className="font-sans text-xs text-ink-soft">What the AI has learned from your feedback</Label>
                <ul className="mt-2 space-y-1">{prefs.learnedNotes.map((n, i) => <li key={i} className="font-sans text-xs text-muted-foreground">• {n}</li>)}</ul></div>
            )}
            <button data-testid="save-prefs" onClick={savePrefs} className="rounded-full bg-ink px-6 py-2.5 font-sans text-sm text-linen hover:bg-ink/85">Save preferences</button>
          </div>
        )}
      </main>

      {shareHelper && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/50 p-4 sm:items-center" data-testid="share-helper" onClick={() => setShareHelper(null)}>
          <div className="my-auto max-h-[92vh] w-full max-w-lg overflow-y-auto bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Instagram size={18} className="text-[#E1306C]" />
              <p className="font-serif text-xl text-ink">Post to Instagram / Facebook</p>
            </div>
            <p className="mt-3 font-sans text-sm text-ink-soft">Instagram never fills the words in for you — so do these <b>2 quick taps</b>:</p>

            <div className="mt-4 rounded-lg bg-linen p-4">
              <p className="font-sans text-xs font-semibold text-ink">STEP 1 — Get the picture (it says “Read the blog”)</p>
              <p className="mt-1 font-sans text-xs text-ink-soft">This is the branded image to post. Tap below, then choose <b>Instagram</b> or <b>Facebook</b> (or “Save to Photos”).</p>
              <div className="relative mx-auto mt-2 aspect-[4/5] w-56 overflow-hidden rounded bg-ink/10">
                {shareHelper.previewUrl && <img src={shareHelper.previewUrl} alt="" className="h-full w-full object-contain" />}
                {shareHelper.building && <div className="absolute inset-0 flex items-center justify-center bg-ink/40 font-sans text-xs text-linen"><Loader2 size={16} className="mr-2 animate-spin" /> Creating your post image…</div>}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="share-photo-btn" disabled={shareHelper.building} onClick={() => sharePhoto()} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-sans text-xs text-linen hover:bg-ink/85 disabled:opacity-50"><ImageIcon size={13} /> Share / save the picture</button>
                <button data-testid="shuffle-cover-btn" disabled={shareHelper.building} onClick={shuffleCover} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft hover:bg-white disabled:opacity-50"><RefreshCw size={13} className={shareHelper.building ? "animate-spin" : ""} /> Don't like it? Try another picture</button>
              </div>
              <CoverGrid options={coverOptions} current={shareHelper.coverImage} disabled={shareHelper.building} onPick={chooseCover} />
            </div>

            <div className="mt-3 rounded-lg bg-linen p-4">
              <p className="font-sans text-xs font-semibold text-ink">STEP 2 — Paste the story (your whole blog)</p>
              <p className="mt-1 font-sans text-xs text-ink-soft">Your full article is already copied. In Instagram/Facebook, tap the caption box and <b>Paste</b> — readers tap “…more” and the whole blog opens. Need it again? Tap Copy.</p>
              <Textarea readOnly value={shareHelper.caption} rows={7} className="mt-2 rounded border-border font-sans text-xs" onFocus={(e) => e.target.select()} data-testid="share-caption" />
              <button data-testid="share-copy-caption" onClick={copyCaption} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft hover:bg-white"><Copy size={13} /> Copy the blog caption</button>
            </div>

            <div className="mt-3 rounded-lg border border-clay/40 bg-amber-50/60 p-4">
              <p className="font-sans text-xs font-semibold text-ink">STEP 3 — Add a tappable link (optional, but recommended)</p>
              <p className="mt-1 font-sans text-xs text-ink-soft">Instagram only makes links tappable in a <b>Story</b>. Share this tall <b>Story picture</b> (it fills the whole screen), tap the <b>🔗 link sticker</b>, paste this link and place the sticker in the empty space under “Tap the link below”:</p>
              <div className="mt-2 flex items-end gap-3">
                <div className="relative aspect-[9/16] w-24 shrink-0 overflow-hidden rounded bg-ink/10" data-testid="story-preview">
                  {shareHelper.storyUrl && <img src={shareHelper.storyUrl} alt="" className="h-full w-full object-cover" />}
                  {shareHelper.building && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Loader2 size={14} className="animate-spin text-white" /></div>}
                </div>
                <button data-testid="share-story-btn" disabled={shareHelper.building || !shareHelper.storyFile} onClick={() => sharePhoto(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[#2C2A26] px-4 py-2 font-sans text-xs text-white hover:opacity-90 disabled:opacity-50"><ImageIcon size={13} /> Share / save Story picture</button>
              </div>
              <Input readOnly value={`${SITE.domain}/blog/${shareHelper.slug}`} className="mt-2 rounded border-border font-sans text-xs" onFocus={(e) => e.target.select()} data-testid="share-blog-link" />
              <button data-testid="share-copy-link" onClick={copyBlogLink} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft hover:bg-white"><Copy size={13} /> Copy blog link</button>
            </div>

            <div className="mt-5 flex justify-end">
              <button data-testid="share-helper-done" onClick={() => setShareHelper(null)} className="rounded-full bg-ink px-6 py-2 font-sans text-sm text-linen hover:bg-ink/85">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
